import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiShield,
  FiSmartphone,
  FiKey,
  FiActivity,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiLoader,
  FiLock,
  FiUnlock,
  FiEye,
  FiEyeOff,
  FiCopy,
  FiRefreshCw,
  FiLogIn,
  FiLogOut,
  FiAlertCircle,
  FiMonitor,
  FiClock,
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { createAuthAxios } from "../../../utils/apiUtils";
import { toast } from "react-toastify";
import "./SecuritySettings.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const SecuritySettings = () => {
  const { user } = useUserContext();
  
  // Detect if user signed in with Google OAuth
  // Backend returns googleId as boolean (true/false)
  const isGoogleUser = user?.googleId === true;
  
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [setup2FAData, setSetup2FAData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [securityActivity, setSecurityActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  useEffect(() => {
    if (user) {
      setIs2FAEnabled(user.twoFactorEnabled || false);
    }
    fetchSecurityActivity();
  }, [user]);

  const fetchSecurityActivity = async () => {
    setLoadingActivity(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get("/security/activity?days=30");
      if (response.data.success) {
        setSecurityActivity(response.data.data.activity || []);
      }
    } catch (error) { /* ignored */ } finally {
      setLoadingActivity(false);
    }
  };

  const initiate2FASetup = async () => {
    // Google OAuth users may not have a password set - backend handles this
    if (!isGoogleUser && !password) {
      toast.error("Please enter your password");
      return;
    }

    setIsLoading(true);
    try {
      const authAxios = createAuthAxios();
      const payload = isGoogleUser ? {} : { password };
      const response = await authAxios.post("/security/2fa/enable", payload);

      if (response.data.success) {
        setSetup2FAData(response.data.data);
        setShow2FASetup(true);
        setPassword("");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to initiate 2FA setup"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const verify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setIsLoading(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post("/security/2fa/verify", {
        token: verificationCode,
      });

      if (response.data.success) {
        setIs2FAEnabled(true);
        setShow2FASetup(false);
        setSetup2FAData(null);
        setVerificationCode("");
        toast.success("Two-factor authentication enabled successfully!");
        fetchSecurityActivity();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Invalid verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FA = async () => {
    // Google OAuth users may not have a password
    if (!isGoogleUser && !password) {
      toast.error("Please enter your password");
      return;
    }
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter your 2FA code");
      return;
    }

    setIsLoading(true);
    try {
      const authAxios = createAuthAxios();
      const payload = {
        token: verificationCode,
      };
      if (!isGoogleUser) {
        payload.password = password;
      }
      const response = await authAxios.post("/security/2fa/disable", payload);

      if (response.data.success) {
        setIs2FAEnabled(false);
        setShowDisable2FA(false);
        setPassword("");
        setVerificationCode("");
        toast.success("Two-factor authentication disabled");
        fetchSecurityActivity();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to disable 2FA");
    } finally {
      setIsLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (setup2FAData?.backupCodes) {
      const codes = setup2FAData.backupCodes.join("\n");
      navigator.clipboard.writeText(codes);
      toast.success("Backup codes copied to clipboard!");
    }
  };

  const getActivityIcon = (type) => {
    const iconMap = {
      login_success: { icon: <FiLogIn />, className: "stg-success" },
      login_failed: { icon: <FiAlertTriangle />, className: "stg-danger" },
      logout: { icon: <FiLogOut />, className: "stg-info" },
      password_change: { icon: <FiKey />, className: "stg-success" },
      password_reset_requested: {
        icon: <FiRefreshCw />,
        className: "stg-warning",
      },
      password_reset_completed: { icon: <FiCheck />, className: "stg-success" },
      key_generated: { icon: <FiKey />, className: "stg-success" },
      key_rotated: { icon: <FiRefreshCw />, className: "stg-info" },
      unauthorized_access: {
        icon: <FiAlertTriangle />,
        className: "stg-danger",
      },
      brute_force_detected: {
        icon: <FiAlertCircle />,
        className: "stg-danger",
      },
    };
    return iconMap[type] || { icon: <FiActivity />, className: "stg-info" };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-purple">
            <FiShield />
          </span>
          Security Settings
        </h1>
        <p className="stg-section-description">
          Manage your account security, two-factor authentication, and view
          security activity
        </p>
      </div>

      {/* 2FA Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <div>
            <h3 className="stg-card-title">
              <FiSmartphone /> Two-Factor Authentication
            </h3>
            <p className="stg-card-subtitle">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="stg-card-actions">
            <span
              className={`stg-status ${
                is2FAEnabled ? "stg-status-success" : "stg-status-warning"
              }`}
            >
              <span className="stg-status-dot"></span>
              {is2FAEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>
        </div>
        <div className="stg-card-body">
          {is2FAEnabled ? (
            <div>
              <div className="stg-alert stg-alert-success stg-mb-0">
                <FiCheck className="stg-alert-icon" />
                <div className="stg-alert-content">
                  <p className="stg-alert-title">2FA is Active</p>
                  <p className="stg-alert-message">
                    Your account is protected with two-factor authentication
                    using an authenticator app.
                  </p>
                </div>
              </div>
              <div style={{ marginTop: "16px" }}>
                <button
                  className="stg-btn stg-btn-danger stg-btn-sm"
                  onClick={() => setShowDisable2FA(true)}
                >
                  <FiUnlock /> Disable 2FA
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p
                style={{ color: "var(--stg-slate-600)", marginBottom: "16px" }}
              >
                Two-factor authentication adds an extra layer of security to
                your account by requiring a code from your authenticator app in
                addition to your password.
              </p>
              {/* Only show password field for non-Google users */}
              {!isGoogleUser && (
                <div className="stg-form-group">
                  <label className="stg-form-label">
                    <FiLock /> Enter your password to enable 2FA
                  </label>
                  <div className="stg-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="stg-input"
                      placeholder="Your current password"
                      style={{ paddingRight: "48px" }}
                    />
                    <button
                      type="button"
                      className="stg-input-action"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
              )}
              <button
                className="stg-btn stg-btn-primary"
                onClick={initiate2FASetup}
                disabled={isLoading || (!isGoogleUser && !password)}
              >
                {isLoading ? (
                  <>
                    <FiLoader className="stg-spin" /> Setting up...
                  </>
                ) : (
                  <>
                    <FiLock /> Enable Two-Factor Authentication
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Security Activity Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiActivity /> Security Activity
          </h3>
          <div className="stg-card-actions">
            <button
              className="stg-btn stg-btn-ghost stg-btn-sm"
              onClick={fetchSecurityActivity}
              disabled={loadingActivity}
            >
              <FiRefreshCw className={loadingActivity ? "stg-spin" : ""} />{" "}
              Refresh
            </button>
          </div>
        </div>
        <div className="stg-card-body">
          {loadingActivity ? (
            <div className="stg-loading">
              <div className="stg-spinner"></div>
            </div>
          ) : securityActivity.length > 0 ? (
            <div className="stg-activity-list">
              {securityActivity.slice(0, 10).map((activity, index) => {
                const { icon, className } = getActivityIcon(activity.type);
                return (
                  <div key={index} className="stg-activity-item">
                    <div className={`stg-activity-icon ${className}`}>
                      {icon}
                    </div>
                    <div className="stg-activity-content">
                      <p className="stg-activity-title">{activity.action}</p>
                      <p className="stg-activity-meta">
                        <span>
                          <FiClock size={12} /> {formatDate(activity.timestamp)}
                        </span>
                        {activity.ip && (
                          <span>
                            <FiMonitor size={12} /> {activity.ip}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="stg-empty">
              <div className="stg-empty-icon">
                <FiActivity />
              </div>
              <h4 className="stg-empty-title">No recent activity</h4>
              <p className="stg-empty-text">
                Your security activity will appear here
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* 2FA Setup Modal */}
      <AnimatePresence>
        {show2FASetup && setup2FAData && (
          <motion.div
            className="stg-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShow2FASetup(false)}
          >
            <motion.div
              className="stg-modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "500px" }}
            >
              <div className="stg-modal-header">
                <h3 className="stg-modal-title">
                  Set Up Two-Factor Authentication
                </h3>
                <button
                  className="stg-modal-close"
                  onClick={() => setShow2FASetup(false)}
                >
                  <FiX />
                </button>
              </div>
              <div className="stg-modal-body">
                <div className="stg-2fa-setup">
                  <p
                    style={{
                      color: "var(--stg-slate-600)",
                      marginBottom: "16px",
                    }}
                  >
                    Scan this QR code with your authenticator app (Google
                    Authenticator, Authy, etc.)
                  </p>
                  <div className="stg-qr-code">
                    <img src={setup2FAData.qrCodeUrl} alt="2FA QR Code" />
                  </div>

                  <div
                    className="stg-alert stg-alert-warning"
                    style={{ textAlign: "left", marginBottom: "16px" }}
                  >
                    <FiAlertTriangle className="stg-alert-icon" />
                    <div className="stg-alert-content">
                      <p className="stg-alert-title">Save Your Backup Codes</p>
                      <p className="stg-alert-message">
                        Store these codes in a safe place. You'll need them if
                        you lose access to your authenticator.
                      </p>
                    </div>
                  </div>

                  <div className="stg-backup-codes">
                    {setup2FAData.backupCodes.map((code, index) => (
                      <div key={index} className="stg-backup-code">
                        {code}
                      </div>
                    ))}
                  </div>

                  <button
                    className="stg-btn stg-btn-secondary stg-btn-sm"
                    onClick={copyBackupCodes}
                    style={{ marginBottom: "24px" }}
                  >
                    <FiCopy /> Copy Backup Codes
                  </button>

                  <div className="stg-form-group" style={{ textAlign: "left" }}>
                    <label className="stg-form-label">
                      Enter 6-digit code from your app
                    </label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) =>
                        setVerificationCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                      className="stg-input"
                      placeholder="000000"
                      maxLength={6}
                      style={{
                        textAlign: "center",
                        fontSize: "24px",
                        letterSpacing: "8px",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="stg-modal-footer">
                <button
                  className="stg-btn stg-btn-secondary"
                  onClick={() => setShow2FASetup(false)}
                >
                  Cancel
                </button>
                <button
                  className="stg-btn stg-btn-primary"
                  onClick={verify2FA}
                  disabled={isLoading || verificationCode.length !== 6}
                >
                  {isLoading ? (
                    <>
                      <FiLoader className="stg-spin" /> Verifying...
                    </>
                  ) : (
                    <>
                      <FiCheck /> Verify & Enable
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disable 2FA Modal */}
      <AnimatePresence>
        {showDisable2FA && (
          <motion.div
            className="stg-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDisable2FA(false)}
          >
            <motion.div
              className="stg-modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="stg-modal-header">
                <h3 className="stg-modal-title">
                  Disable Two-Factor Authentication
                </h3>
                <button
                  className="stg-modal-close"
                  onClick={() => setShowDisable2FA(false)}
                >
                  <FiX />
                </button>
              </div>
              <div className="stg-modal-body">
                <div className="stg-alert stg-alert-warning">
                  <FiAlertTriangle className="stg-alert-icon" />
                  <div className="stg-alert-content">
                    <p className="stg-alert-title">
                      This will reduce your account security
                    </p>
                    <p className="stg-alert-message">
                      Disabling 2FA will make your account more vulnerable to
                      unauthorized access.
                    </p>
                  </div>
                </div>

                {/* Only show password field for non-Google users */}
                {!isGoogleUser && (
                  <div className="stg-form-group">
                    <label className="stg-form-label">Password</label>
                    <div className="stg-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="stg-input"
                        placeholder="Enter your password"
                        style={{ paddingRight: "48px" }}
                      />
                      <button
                        type="button"
                        className="stg-input-action"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                )}

                <div className="stg-form-group">
                  <label className="stg-form-label">2FA Code</label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    className="stg-input"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                </div>
              </div>
              <div className="stg-modal-footer">
                <button
                  className="stg-btn stg-btn-secondary"
                  onClick={() => setShowDisable2FA(false)}
                >
                  Cancel
                </button>
                <button
                  className="stg-btn stg-btn-danger"
                  onClick={disable2FA}
                  disabled={
                    isLoading || (!isGoogleUser && !password) || verificationCode.length !== 6
                  }
                >
                  {isLoading ? (
                    <>
                      <FiLoader className="stg-spin" /> Disabling...
                    </>
                  ) : (
                    <>
                      <FiUnlock /> Disable 2FA
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SecuritySettings;
